import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { graphql, StaticQuery } from 'gatsby';

import { IntlContext } from '../../Providers/Intl';
import Container from '../Container';
import Link from '../Link';

import {
  StyledFooter,
  Logo,
  TopRow,
  SocialLinks,
  BottomRow,
  StyledBottomContainer,
  LeftContainer,
  LinksAndBadgesWrapper,
  CenterContainer,
  RightContainer,
  Column,
  Badge,
  Heading,
} from './styles';

// * image {String} src for the logo
// * socialShare {arr} array of data for each social icon
// * menu {arr} array of links for the footer
// * pageLinks {arr} array of links at the bottom of the footer
// * certifications {arr} array of badges for the footer

export const Footer = ({ data }) => {
  if (!data) return null;

  const {
    image,
    imageSharp,
    socialShare,
    menu,
    pageLinks,
    certifications,
  } = data;

  return (
    <StyledFooter>
      <Container className="auto-margin">
        <TopRow>
          <LeftContainer>
            {imageSharp && (
              <Link to="/">
                <Logo src={imageSharp.publicURL} alt={image.altTag} />
              </Link>
            )}
            <SocialLinks>
              {socialShare.map(
                ({
                  link,
                  imageIcon,
                  imageIconSharp,
                  queryString,
                  ariaLabel,
                }) => {
                  return (
                    <Link
                      key={`social-share-${link.url}`}
                      to={link.url}
                      queryString={queryString}
                      ariaLabel={ariaLabel}
                    >
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </Link>
                  );
                }
              )}
            </SocialLinks>
          </LeftContainer>
          <LinksAndBadgesWrapper>
            {menu && (
              <CenterContainer>
                {menu.map(({ text, subitems }, index) => {
                  return (
                    <Column key={`columns-${index.toString()}`}>
                      <Heading>{text}</Heading>
                      {subitems.map(
                        ({ text: linkText, url, queryString, ariaLabel }) => {
                          return (
                            <Link
                              key={`${linkText}-${url}`}
                              to={`${url}/`}
                              queryString={queryString}
                              ariaLabel={ariaLabel}
                            >
                              {linkText}
                            </Link>
                          );
                        }
                      )}
                    </Column>
                  );
                })}
              </CenterContainer>
            )}
            <RightContainer className="no-bullets">
              {certifications.map(
                ({ image: badge, imageSharp: badgeSharp }) => {
                  return (
                    <li key={`badge-${badge.url}`}>
                      <Badge src={badgeSharp} alt={badge.altTag} />
                    </li>
                  );
                }
              )}
            </RightContainer>
          </LinksAndBadgesWrapper>
        </TopRow>
      </Container>
      <BottomRow>
        <StyledBottomContainer className="auto-margin">
          {pageLinks.map(({ text, link, queryString, ariaLabel }) => {
            return (
              <Link
                key={`${text}-${link}`}
                to={`${link}/`}
                queryString={queryString}
                ariaLabel={ariaLabel}
                external
              >
                {text}
              </Link>
            );
          })}
        </StyledBottomContainer>
      </BottomRow>
    </StyledFooter>
  );
};

Footer.propTypes = {
  data: PropTypes.shape().isRequired,
};

Footer.defaultProps = {};

const withStaticQueryFooter = () => {
  return (
    <StaticQuery
      query={graphql`
        query footerQuery {
          prodigy {
            headerFooter {
              footer {
                lang
                image {
                  url
                  altTag
                }
                imageSharp {
                  publicURL
                }
                socialShare {
                  link {
                    url
                  }
                  queryString
                  ariaLabel
                  imageIcon {
                    url
                    altTag
                  }
                  imageIconSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP, AVIF]
                      )
                    }
                  }
                }
                menu {
                  type
                  text
                  url
                  subitems {
                    type
                    text
                    url
                    queryString
                    ariaLabel
                  }
                }
                pageLinks {
                  text
                  link
                  ariaLabel
                  queryString
                }
                certifications {
                  link {
                    url
                  }
                  image {
                    url
                    altTag
                  }
                  imageSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP, AVIF]
                      )
                    }
                  }
                  ariaLabel
                  queryString
                }
              }
            }
          }
        }
      `}
      render={({ prodigy }) => {
        const { intl } = useContext(IntlContext);
        const { footer } = prodigy.headerFooter.find(
          ({ footer: footerData }) =>
            footerData.lang ===
            /* check if lang being defaulted to is x-default (a catchall to default to main-en) */
            (intl.lang === 'x-default' ? 'en-ca' : intl.lang)
        );
        return <Footer data={footer} />;
      }}
    />
  );
};

export default withStaticQueryFooter;
